import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { ShieldCheck, RefreshCw, CheckCircle2, AlertTriangle, Clock, Camera } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === 'warn') return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (status === 'error') return <AlertTriangle className="h-4 w-4 text-rose-600" />;
  if (status === 'preview') return <Clock className="h-4 w-4 text-slate-400" />;
  if (status === 'skipped') return <CheckCircle2 className="h-4 w-4 text-slate-400" />;
  return <Clock className="h-4 w-4 text-slate-400" />;
};

const stepDetail = (s) => {
  const r = s.result || {};
  if (s.step === 1) return `${r.collision_count ?? 0} colliding id(s)`;
  if (s.step === 2) {
    if (r.rows_deleted != null) return `${r.rows_deleted} duplicate row(s) deleted`;
    return r.would_delete || '—';
  }
  if (s.step === 3) {
    if (r.created) return `Created: ${r.created.join(', ')}`;
    if (r.will_create) return `Will create: ${r.will_create.join(', ')}`;
    return r.error || r.reason || '—';
  }
  if (s.step === 4) {
    if (r.customers_updated != null) return `${r.customers_updated} customer(s) backfilled`;
    if (r.customers_missing_numeric_id != null) return `${r.customers_missing_numeric_id} customer(s) missing numeric_id`;
    return '—';
  }
  if (s.step === 5) {
    if (r.user_count != null) {
      return `${r.user_count} user(s) + ${r.profile_count} profile(s) snapshotted`;
    }
    return r.will_snapshot ? 'Will take snapshot' : '—';
  }
  return '—';
};

export const IntegrityWorkflowCard = ({ onCompleted }) => {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [steps, setSteps] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/admin/duplicates/integrity/run-all');
      setSteps(data.steps || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run preview');
    } finally {
      setPreviewing(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post('/admin/duplicates/integrity/run-all?apply=true');
      setSteps(data.steps || []);
      toast.success(data.message || 'Integrity workflow complete');
      if (onCompleted) onCompleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Workflow failed');
    } finally {
      setApplying(false);
    }
  };

  const allGreen = steps && steps.every((s) => s.status === 'ok' || s.status === 'skipped');
  const hasIssues = steps && steps.some((s) => s.status === 'warn' || s.status === 'error');

  return (
    <>
      <Card className="border-indigo-200 bg-indigo-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            Data Integrity Checks
            {steps && (
              <Badge variant={hasIssues ? 'destructive' : 'secondary'}>
                {steps.filter((s) => s.status === 'ok').length}/{steps.length} OK
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Detect id collisions, enforce unique indexes, backfill customer numeric_ids, and take a
            users-collection snapshot — all from one button. Always previews first.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={previewing || applying}
              data-testid="integrity-preview-btn"
            >
              {previewing ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Scanning…</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" />Preview Checks</>
              )}
            </Button>
            {steps && (
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={applying || allGreen}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                data-testid="integrity-apply-btn"
                title={allGreen ? 'Nothing to fix' : undefined}
              >
                {applying ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Running…</>
                ) : (
                  <>{allGreen ? 'All Green' : 'Apply Fixes & Take Snapshot'}</>
                )}
              </Button>
            )}
          </div>

          {steps && (
            <div className="rounded-md border border-indigo-200 bg-white divide-y"
                 data-testid="integrity-steps">
              {steps.map((s) => (
                <div key={s.step} className="flex items-center gap-3 p-3 text-sm">
                  <StatusIcon status={s.status} />
                  <div className="flex-1">
                    <div className="font-medium">
                      {s.step}. {s.name}
                    </div>
                    <div className="text-xs text-slate-500">{stepDetail(s)}</div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      s.status === 'ok' ? 'border-emerald-300 text-emerald-700'
                      : s.status === 'warn' ? 'border-amber-300 text-amber-700'
                      : s.status === 'error' ? 'border-rose-300 text-rose-700'
                      : 'border-slate-300 text-slate-500'
                    }
                  >
                    {s.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Camera className="h-3 w-3" />
            <span>
              Nightly snapshot at 3 AM UTC keeps a 7-day rolling restore window of the entire
              users collection.
            </span>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply integrity fixes?</AlertDialogTitle>
            <AlertDialogDescription>
              This will:
              <br />· Delete duplicate <code>users.id</code> rows (keeping the oldest of each set)
              <br />· Create unique indexes on <code>users.id</code> and (sparse) <code>users.numeric_id</code>
              <br />· Backfill missing customer numeric_ids
              <br />· Capture a fresh users-collection snapshot
              <br /><br />
              The snapshot is captured first, so any deletion is recoverable from the 7-day window.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); apply(); }}
              disabled={applying}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="integrity-confirm-btn"
            >
              Yes, apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
